
import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';

const COLORS = ['#2563EB', '#16A34A', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#0D1B3D'];

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
const fmtMonth = (m) => {
  const [y, mo] = m.split('-');
  return new Date(y, mo - 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

// ---- Small building blocks ----------------------------------------------

function SectionCard({ title, subtitle, icon, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-5 ${className}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent flex items-center gap-2">
            {icon && <span className="text-base">{icon}</span>}
            {title}
          </h3>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function MetricCard({ icon, label, value, tone = 'blue', sub }) {
  const tones = {
    blue: 'bg-blue-50 text-[#2563EB]',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-[#7C3AED]',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg shrink-0 ${tones[tone]}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400 font-medium">{label}</p>
         <p className="text-xl font-bold text-gray-700 truncate">{value}</p>
        {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-gray-300">
      <svg className="w-9 h-9 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4 19V5a1 1 0 011-1h10.5L21 8.5V19a1 1 0 01-1 1H5a1 1 0 01-1-1z" />
        <path d="M14 4v5h5" />
      </svg>
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}

const tooltipStyle = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
};

// ---------------------------------------------------------------------------

function AgentReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/agent/reports/summary');
        setData(res.data);
      } catch (err) {
        console.error('Failed to load reports', err);
        setError('Could not load report data.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
   return (
      <AgentLayout>
        <div className="bg-[#FDF5FF] -m-6 p-6 min-h-screen">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-1">Reports</h2>
        <p className="text-gray-500 text-sm mb-6">Loading your performance overview…</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 h-24 animate-pulse" />
          ))}
        </div>
        </div>
      </AgentLayout>
    );
  }

  if (error || !data) {
   return (
      <AgentLayout>
        <div className="bg-[#FDF5FF] -m-6 p-6 min-h-screen">
        <h2 className="text-2xl font-bold ... mb-1">Reports</h2>
        <div className="bg-red-50 text-red-600 text-sm rounded-xl p-4 mt-4">{error || 'No data available.'}</div>
        </div>
      </AgentLayout>
   );
  }

  const candidatesTrend = data.candidates_trend.map((d) => ({ ...d, label: fmtDate(d.date) }));
  const interviewsTrend = data.interviews_trend.map((d) => ({ ...d, label: fmtDate(d.date) }));
  const placementsTrend = data.placements_trend.map((d) => ({ ...d, label: fmtDate(d.date) }));
  const commissionTrend = data.commission_trend.map((d) => ({ ...d, label: fmtMonth(d.month), total: Number(d.total) }));

  const totalCandidates30d = candidatesTrend.reduce((s, d) => s + d.count, 0);
  const totalInterviews30d = interviewsTrend.reduce((s, d) => s + d.count, 0);

  return (
    <AgentLayout>
      <div className="bg-[#FDF5FF] -m-6 p-6 min-h-screen">
      <div className="mb-6">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Reports</h2>
        <p className="text-gray-500 text-sm mt-0.5">
          Your performance at a glance — candidates, interviews, placements and commission.
        </p>
      </div>

      {/* ---- Top metric strip ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard icon="👤" label="Candidates" tone="blue" value={totalCandidates30d} sub="Added in last 30 days" />
        <MetricCard icon="🗓️" label="Interviews" tone="purple" value={totalInterviews30d} sub="Scheduled in last 30 days" />
        <MetricCard icon="🎯" label="Placements" tone="green" value={data.placements_total} sub="Total candidates joined" />
        <MetricCard
          icon="💰"
          label="Commission (Month)"
          tone="amber"
          value={fmtMoney(data.commission_summary.month)}
          sub={`${fmtMoney(data.commission_summary.pending)} pending`}
        />
      </div>

      {/* ---- Commission detail strip ---- */}
      <SectionCard title="Commission Breakdown" icon="💰" subtitle="Where you stand right now" className="mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center py-2">
            <p className="text-xs text-gray-400 font-medium mb-1">Today</p>
            <p className="text-lg font-bold text-gray-700">{fmtMoney(data.commission_summary.today)}</p>
          </div>
          <div className="text-center py-2 border-x border-gray-100">
            <p className="text-xs text-gray-400 font-medium mb-1">This Month</p>
            <p className="text-lg font-bold text-green-600">{fmtMoney(data.commission_summary.month)}</p>
          </div>
          <div className="text-center py-2">
            <p className="text-xs text-gray-400 font-medium mb-1">Pending</p>
            <p className="text-lg font-bold text-amber-600">{fmtMoney(data.commission_summary.pending)}</p>
          </div>
        </div>
      </SectionCard>

      {/* ---- Trend charts ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Candidates Added" icon="👤" subtitle="Last 30 days">
          {candidatesTrend.length === 0 ? (
            <EmptyState label="No candidates added in this period." />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={candidatesTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3, fill: '#2563EB' }} name="Candidates" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="Interviews Scheduled" icon="🗓️" subtitle="Last 30 days">
          {interviewsTrend.length === 0 ? (
            <EmptyState label="No interviews scheduled in this period." />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={interviewsTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#7C3AED" radius={[6, 6, 0, 0]} name="Interviews" maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="Placements" icon="🎯" subtitle="Last 30 days">
          {placementsTrend.length === 0 ? (
            <EmptyState label="No placements in this period." />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={placementsTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#16A34A" radius={[6, 6, 0, 0]} name="Placements" maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="Commission Trend" icon="💰" subtitle="Last 6 months">
          {commissionTrend.length === 0 ? (
            <EmptyState label="No commission recorded yet." />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={commissionTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  tickFormatter={(v) => `₹${v >= 1000 ? `${v / 1000}k` : v}`}
                />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Line type="monotone" dataKey="total" stroke="#D97706" strokeWidth={2.5} dot={{ r: 3, fill: '#D97706' }} name="Commission" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>

      {/* ---- Breakdown charts ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Source-wise Candidates" icon="📍" subtitle="Where your candidates come from">
          {data.source_wise.length === 0 ? (
            <EmptyState label="No source data yet." />
          ) : (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={data.source_wise}
                    dataKey="count"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {data.source_wise.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap sm:flex-col gap-2 w-full sm:w-auto">
                {data.source_wise.map((s, i) => (
                  <div key={s.source} className="flex items-center gap-2 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-gray-600 capitalize">{s.source.replace('_', ' ')}</span>
                    <span className="font-semibold text-gray-700 ml-auto sm:ml-1">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>

        <SectionCard title="Candidate Pipeline" icon="📊" subtitle="Candidates by stage">
          {data.pipeline.length === 0 ? (
            <EmptyState label="No pipeline data yet." />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, data.pipeline.length * 36)}>
              <BarChart data={data.pipeline} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} width={120} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#7C3AED" radius={[0, 6, 6, 0]} name="Candidates" maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>
      </div>
    </AgentLayout>
  );
}

export default AgentReports;